import { makeStyles } from '@material-ui/core/styles'
import type { Theme } from '@material-ui/core'
import { getPresentationContentStyles } from '@paintscout/ui/styles'
import type { EditorProps } from './Editor'

export const useEditorStyles = makeStyles<Theme, EditorProps>((theme) => {
  return {
    editorContent: (props) => ({
      cursor: props.disabled ? 'default' : 'text',
      flexGrow: '2'
    }),
    root: (props) => ({
      ...(props.label && { marginTop: theme.spacing(1) }),
      ...getPresentationContentStyles(theme, { noMargins: ['li'] }),
      borderRadius: theme.borderRadius.md,
      ...(props.showFileBar &&
        (props.files?.length > 0 || props.content.length > 0) && {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0
        }),
      position: 'relative',
      width: '100%',
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
      '& input': {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(16),
        '&:focus-visible': {
          outline: 'none !important'
        }
      },
      backgroundColor: props.disabled ? theme.paintscout.palette.brand.Neutral.P94 : theme.palette.background.paper,
      minHeight: 120,
      ...(props.singleLine && {
        minHeight: 0
      }),
      '&:hover:not(:focus-within)': {
        borderColor: !props.disabled && 'transparent',
        boxShadow: props.disabled ? 'none' : `0 0 0 2px ${theme.paintscout.palette.brand.Neutral.P70}`,
        boxSizing: 'border-box'
      },
      '&:focus-within': {
        borderColor: 'transparent',
        boxShadow: `0 0 0 2px ${theme.paintscout.palette.primary}`
      },

      '& .ProseMirror': {
        opacity: (props: EditorProps) => (props.disabled ? 0.5 : 1),
        outline: 'none',
        padding: theme.size['2'],
        fontSize: theme.typography.fontSize,
        display: 'block',
        flexGrow: 1,
        maxWidth: '100%',
        overflowWrap: 'break-word',
        '&:hover': {},
        '& .ProseMirror-trailingBreak': {
          display: 'none'
        },
        '& .ps-persistent-selection': {
          backgroundColor: 'rgba(155, 195, 255, 0.5)'
        }
      },
      '& .ProseMirror p': {
        whiteSpace: 'break-spaces',
        // Slight increase in line height when placeholders are present to prevent border overlap
        '&:has(.template-placeholder)': {
          lineHeight: 1.5
        }
      },
      '& .template-placeholder': {
        paddingLeft: theme.size['1'],
        paddingRight: theme.size['1'],
        paddingTop: theme.size['0.25'],
        paddingBottom: theme.size['0.25'],
        marginTop: theme.spacing(0.25),
        marginBottom: theme.spacing(0.25),
        marginLeft: theme.spacing(0.25),
        marginRight: theme.spacing(0.125),
        fontWeight: theme.typography.fontWeightMedium,
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.palette.common.black}`,
        display: 'inline-block',
        verticalAlign: 'baseline'
      },
      // this and all the flexGrow stuff is so the ProseMirror (clickable/editable area) is the full height of root (adjustable by props)
      // hacky solution as there is an aria inserted div by tiptap that needs to be set to flex/flexGrow 1 but can't be targeted
      '& div': {
        display: 'flex'
        // flexGrow: 1
      },
      '& #menu-bar': {
        position: 'sticky',
        top: theme.spacing(-3),
        zIndex: 2,
        borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
        ...(props.error && {
          backgroundColor: theme.paintscout.palette.semantic.Danger.S90,
          borderBottom: `1px solid ${theme.paintscout.palette.semantic.Danger.S60}`
        }),
        ...(props.disabled && {
          backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
          borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P70}`
        })
      },
      ...(props.error && {
        border: `1px solid ${theme.paintscout.palette.semantic.Danger.S60}`,
        backgroundColor: theme.paintscout.palette.semantic.Danger.S90
      })
    }),
    dropRoot: () => ({
      // Hover effect when dropping
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px ${theme.paintscout.palette.primary}`,
      // Hide odd lines that appear when dropping
      '& .prosemirror-dropcursor-block': {
        display: 'none'
      }
    }),
    addRounding: {
      borderRadius: theme.borderRadius.md
    },
    hideBottomRounding: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    },
    withFileBar: () => ({
      minHeight: 168
    }),
    noDisplay: {
      '&$noDisplay': {
        display: 'none'
      }
    },
    fileDivContainer: {
      borderRadius: theme.borderRadius.md,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      width: '100%',
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      display: 'flex',
      padding: theme.spacing(0.5)
    },
    filesContainer: {
      width: '100%',
      display: 'flex',
      overflowX: 'auto',
      alignSelf: 'flex-end'
    },
    fileItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0.75),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      marginRight: theme.spacing(1),
      borderRadius: theme.borderRadius.md,
      whiteSpace: 'nowrap'
    },
    fileItemText: {
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P60,
        cursor: 'pointer'
      }
    },
    fileCloseIcon: {
      color: theme.paintscout.palette.brand.Neutral.P80,
      marginLeft: theme.spacing(1),
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P60,
        cursor: 'pointer'
      }
    },
    characterCountDiv: {
      marginTop: theme.spacing(0.5)
    },
    errorDiv: (props) => ({
      marginTop: props.characterCount ? theme.spacing(0) : theme.spacing(0.5)
    })
  }
})
